<template>
  <v-dialog @keydown.esc="isOpen=false" v-model="isOpen" persistent max-width="400px">
    <template v-if="btn" v-slot:activator="{ on }">
      <v-tooltip left>
        <template
          v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            fab
            x-small
            depressed
            color="primary"
            @click="isOpen = true"
          >
            <v-icon
              icon
              small
            >mdi-pencil-outline</v-icon>
          </v-btn>
        </template>
        <span>Editar receta</span>
      </v-tooltip>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Editar {{ item.name }}</span>
      </v-card-title>
      <v-card-text>
        <v-form ref="editRecipeForm" v-model="form.valid">
          <v-container grid-list-xl>
            <v-layout wrap>
              <v-flex xs12 md12>
                <v-text-field
                  data-cy="internalId"
                  v-if="!form.data.subrecipe"
                  :label="$t('recipes.labels.internalId')"
                  v-model="form.data.internalId"
                  :rules="validation.internalId"
                >
                </v-text-field>
                <v-text-field
                  data-cy="name"
                  :label="$t('recipes.labels.name')"
                  v-model="form.data.name"
                  :rules="validation.name"
                  required>
                </v-text-field>
                <v-text-field
                  data-cy="description"
                  v-if="!item.subrecipe"
                  :label="$t('recipes.labels.description')"
                  v-model="form.data.description"
                  :rules="validation.description"
                  required>
                </v-text-field>
                <v-text-field
                  data-cy="price"
                  v-if="!form.data.subrecipe"
                  :label="$t('recipes.labels.price')"
                  v-model="form.data.price"
                  :suffix="`Costo $${formatString(item.cost)}`"
                  :rules="validation.price"
                  prefix="$"
                >
                </v-text-field>
                <v-text-field
                  v-if="form.data.subrecipe"
                  :label="$t('recipes.labels.production')"
                  v-model.number="form.data.production"
                  :rules="validation.production"
                  required>
                </v-text-field>
              </v-flex>
              <v-flex
                v-if="!form.data.subrecipe"
                xs12 md12 class="d-flex"
              >
                <NewSection />
                <v-select
                  data-cy="section"
                  :label="$t('recipes.labels.section')"
                  v-model="form.data.section"
                  :items="sections"
                  :rules="validation.section"
                  item-text="name"
                  item-value="id"
                />
              </v-flex>
              <v-flex
                xs12
                md12
                class="d-flex"
                v-if="form.data.subrecipe"
              >
                <v-select
                  :label="$t('recipes.labels.unitStock')"
                  v-model="form.data.unitStock"
                  :items="units"
                  :rules="validation.unitStock"
                  item-text="name"
                  item-value="id"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="mr-4"
          outlined
          rounded
          small
          depressed
          @click="closeDialog">
          {{ $t('recipes.close') }}
        </v-btn>
        <v-btn
          data-cy="success"
          color="success"
          class="mr-4"
          rounded
          small
          depressed
          @click="submit">
          {{ $t('recipes.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import NewSection from '@/components/modals/NewSection'
import { required, string, isnil, number, numeric, isnull, object } from 'vue-mc/validation'
import { cloneDeep } from 'lodash'

export default {
  props: {
    item: {
      type: Object,
      required: true
    },
    btn: {
      type: Boolean,
      default: true
    }
  },
  components: {
    NewSection
  },
  data () {
    return {
      overlay: false,
      isOpen: false,
      form: {
        valid: false,
        data: {
          internalId: null,
          name: null,
          description: null,
          price: null,
          section: null,
          subrecipe: false,
          production: null,
          unitStock: null
        }
      },
      validation: {
        internalId: [required.and(string)],
        name: [required.and(string)],
        description: [isnil.or(string)],
        price: [!this.item.subrecipe ? required.and(number.or(numeric)) : isnil.or(number.or(numeric))],
        production: [number],
        section: [!this.item.subrecipe ? required.and(object.or(string)) : isnil.or(object)],
        unitStock: [isnull.or(string)]
      }
    }
  },
  computed: {
    units: function () {
      const availableUnitCodes = ['kg', 'l', 'pz']
      return this.$store.state.login.units.data.filter(unit => availableUnitCodes.includes(unit.code)) || []
    },
    sections: function () {
      return (this.$store.state.currentRestaurant.sections.data || [])
        .filter(section => section.area && (section.area === this.$store.state.area._id || section.area._id === this.$store.state.area._id))
    }
  },
  watch: {
    isOpen (val) {
      if (val) {
        this.resetRecipeForm()
      }
    }
  },
  methods: {
    submit: async function () {
      this.$refs.editRecipeForm.validate()

      if (this.form.valid) {
        this.overlay = true
        try {
          const payload = cloneDeep(this.form.data)
          if (typeof (payload.section) !== 'string' && payload.subrecipe === false) payload.section = payload.section._id
          await this.$api.put(`/v1/recipes/${this.item.uuid}`, payload)
          this.closeDialog()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    resetRecipeForm () {
      this.form.data = {
        uuid: this.item.uuid,
        internalId: this.item.internalId,
        name: this.item.name,
        description: this.item.description,
        price: this.item.price,
        area: this.item.area,
        section: this.item.section,
        subrecipe: this.item.subrecipe,
        production: this.item.subrecipe
          ? this.item.production
          : 0,
        unitStock: this.item.subrecipe
          ? (this.item.unitStock && this.item.unitStock.id)
            ? this.item.unitStock.id
            : this.item.unitStock
          : null
      }
    },
    closeDialog () {
      this.isOpen = false
      this.$emit('close', false)
    },
    open () {
      this.isOpen = true
    },
    formatString (value) {
      if (value) {
        const num = value.toString().split('.')
        const valWithCommas = num[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        let result
        if (valWithCommas && num[1]) {
          result = num[1].slice(0, 2)
          result = valWithCommas.concat('.', result)
        } else {
          result = valWithCommas
        }
        return result
      }
    }
  }
}
</script>
